import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';

// Landing page for the "forgot password" email link. Supabase's client parses
// the recovery token out of the URL on load and fires PASSWORD_RECOVERY, which
// gives us a short-lived session that's allowed to call updateUser({password}).
// Chase must add this page's URL to Supabase Auth → URL Configuration →
// Redirect URLs (both the netlify.app host and shinyvaultlgs.com) for the link
// to be accepted.
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);   // recovery session present
  const [checked, setChecked] = useState(false); // initial session check done
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    // The recovery event may have already fired before this component mounted,
    // so check the current session too — either path means we can reset.
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (data?.session) setReady(true);
      setChecked(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) setReady(true);
    });
    return () => { active = false; sub?.subscription?.unsubscribe(); };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Those passwords don’t match.'); return; }
    setBusy(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) {
        setError(err.message + (err.status ? ` (HTTP ${err.status})` : ''));
        return;
      }
      setDone(true);
      setTimeout(() => navigate('/account/orders', { replace: true }), 1800);
    } catch (err) {
      setError(err?.message || 'Could not update your password. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const box = (color, Icon, text) => (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'flex-start', color: `var(--${color})`,
      fontSize: '0.85rem', lineHeight: 1.45, padding: '10px 12px', borderRadius: 8,
      border: `1px solid var(--${color})`, background: `color-mix(in srgb, var(--${color}) 8%, transparent)`,
    }}>
      <Icon size={16} style={{ flexShrink: 0, marginTop: 2 }} />
      <span>{text}</span>
    </div>
  );

  return (
    <div className="sv-container" style={{ paddingBlock: '70px', maxWidth: 400 }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 6 }}>Set a new password</h1>

      {done ? (
        <div style={{ marginTop: 18 }}>
          {box('success', CheckCircle2, 'Password updated. Taking you to your account…')}
        </div>
      ) : checked && !ready ? (
        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {box('danger', AlertCircle, 'This reset link is invalid or has expired. Request a new one from the login page.')}
          <Link to="/login" className="sv-btn" style={{ textAlign: 'center' }}>Back to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 18 }}>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginBottom: 6 }}>
            Enter a new password for your account.
          </p>
          <div style={{ position: 'relative' }}>
            <input
              className="sv-input"
              type={showPw ? 'text' : 'password'}
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              minLength={6}
              required
              style={{ width: '100%', paddingRight: 46 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
              title={showPw ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center', padding: 6,
              }}
            >
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <input
            className="sv-input"
            type={showPw ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            required
          />
          {error && box('danger', AlertCircle, error)}
          <button type="submit" className="sv-btn" disabled={busy} style={{ marginTop: 4 }}>
            {busy ? 'Please wait…' : 'Update password'}
          </button>
        </form>
      )}
    </div>
  );
}
